/**
 * Mapper from the ER-11187 generic Task list API response to the shape
 * consumed by the shared TasksPage/YourTasksTable components.
 *
 * API row (TaskV2Serializer):
 *   { id, type, status, status_category, priority, summary, deadline,
 *     assignee_id, assignee_name, assigned_by_id,
 *     assigned_by: { id, name, avatar }, created_at, updated_at,
 *     started_on, done_on, checkpoint_id, criteria, responsibility_id,
 *     responsibility_name, chatter_count }
 *
 * Table row:
 *   { id, summary, criteria, responsibility, assignedTo: { name, avatar },
 *     assignedBy: { name, avatar, date }, assignedOn, status, type, deadline,
 *     doneOn, checkpointId, responsibilityId, isCurrentUserAssignee, showUpload,
 *     chatterCount, chatterUnread, chatterTable }
 */

export const TASK_CHATTER_TABLE = "common_uploadmicrolearningvideotask";

const STATUS_KEY_BY_VALUE = {
  "To Do": "to_do",
  PENDING: "to_do",
  "In Progress": "in_progress",
  Completed: "completed",
  COMPLETED: "completed",
  Canceled: "canceled",
  Cancelled: "canceled",
  CANCELLED: "canceled",
  Planned: "planned",
};

export const normalizeTaskStatus = (status) => {
  if (!status) return "to_do";
  return STATUS_KEY_BY_VALUE[status] || String(status).toLowerCase().replace(/\s+/g, "_");
};

/** Employee id from localStorage accessPermission (current account access row). */
export function getAccessPermissionEmployeeId() {
  try {
    const raw = localStorage.getItem("accessPermission");
    if (!raw) return null;
    const accessPermission = JSON.parse(raw);
    const id = accessPermission?.employee?.id;
    if (id == null || id === "") return null;
    const numericId = Number(id);
    return Number.isFinite(numericId) ? numericId : null;
  } catch {
    return null;
  }
}

export function isCurrentUserTaskAssignee(
  task,
  currentEmployeeId = getAccessPermissionEmployeeId()
) {
  if (currentEmployeeId == null || task?.assignee_id == null) return false;
  return Number(task.assignee_id) === Number(currentEmployeeId);
}

export const formatResponsibilityLabel = (task = {}) => {
  const name = String(task.responsibility_name || "").trim();
  const id = task.responsibility_id;
  if (name && /responsibility\s*#/i.test(name)) {
    return name;
  }
  if (id != null && name) {
    return `Responsibility #${id} - ${name}`;
  }
  if (name) return name;
  if (id != null) return `Responsibility #${id}`;
  return "";
};

const assignedByPerson = (assignedBy) => {
  if (assignedBy && typeof assignedBy === "object") {
    return {
      name: assignedBy.name || "",
      avatar: assignedBy.avatar || "",
    };
  }
  if (typeof assignedBy === "string") {
    return { name: assignedBy, avatar: "" };
  }
  return { name: "", avatar: "" };
};

export const mapApiTaskToTableRow = (task, options = {}) => {
  const status = normalizeTaskStatus(task.status);
  const assignedBy = assignedByPerson(task.assigned_by);
  const chatterCount = Number(task.chatter_count) || 0;
  const currentEmployeeId =
    options.currentEmployeeId !== undefined
      ? options.currentEmployeeId
      : getAccessPermissionEmployeeId();
  const isCurrentUserAssignee = isCurrentUserTaskAssignee(task, currentEmployeeId);
  return {
    id: task.id,
    summary: task.summary || "",
    criteria: String(task.criteria || "").trim(),
    responsibility: formatResponsibilityLabel(task),
    assignedTo: { name: task.assignee_name || "", avatar: task.assignee_avatar || "" },
    assigneeId: task.assignee_id ?? null,
    assignedBy: { ...assignedBy, date: task.assigned_at || task.created_at || "" },
    assignedOn: task.assigned_at || task.created_at || "",
    status,
    type: task.type || "",
    deadline: task.deadline || null,
    doneOn: task.done_on || null,
    checkpointId: task.checkpoint_id ?? null,
    responsibilityId: task.responsibility_id ?? null,
    isCurrentUserAssignee,
    showUpload: status === "to_do" && task.checkpoint_id != null && isCurrentUserAssignee,
    chatterCount,
    chatterUnread: chatterCount,
    chatterTable: TASK_CHATTER_TABLE,
  };
};

export const mapApiTasksToTableRows = (tasks = [], options = {}) =>
  (Array.isArray(tasks) ? tasks : []).map((task) => mapApiTaskToTableRow(task, options));
